import { capitalizeName } from '@getpopsure/public-utility';
import { getPoliciesListAction } from 'features/policiesDashboard/utils/getPoliciesListAction';
import { getPolicyStatus } from 'features/policiesDashboard/utils/getPolicyStatus';
import {
  bannerColorMapping,
  getTitleMapping,
} from 'models/insurances/types/mapping';
import { getCancelApplicationAllowed } from 'selectors/policies';

import { ContactUsButton } from '../../components/buttons';
import type { PolicySingleModal } from '../../components/modals';
import {
  BillingType,
  GetPolicyConfigArgs,
  PolicyConfig,
  PolicySingleSection,
} from '../../PolicySingle.models';
import { getPolicyDocuments } from '../../sections/PolicyDocuments/utils/getPolicyDocuments';
import { PublicHealthCancelApplicationButton } from './buttons/PublicHealthCancelApplicationButton';
import { PublicHealthCancelApplicationModal } from './modals/PublicHealthCancelApplicationModal';

export const getPublicHealthConfig = (
  args: GetPolicyConfigArgs
): PolicyConfig => {
  const [policy, t] = args;

  const action = getPoliciesListAction(policy);
  const isProviderBkk = policy?.providerId === 'BKK_FIRMUS';
  const policyStatus =
    action && action !== 'DETAIL' && !isProviderBkk
      ? 'ACTION_NEEDED'
      : getPolicyStatus(policy, true);

  const capitalizedName = policy.attributes?.insuredPerson?.name
    ? capitalizeName(policy.attributes?.insuredPerson.name)
    : '';
  const subtitle = capitalizedName || '';
  const cancelApplicationAllowed = getCancelApplicationAllowed(policy);

  const header: PolicyConfig['header'] = {
    title: getTitleMapping(t).PUBLIC_HEALTH,
    providerName: policy.providerName,
    subtitle,
    backgroundColor: bannerColorMapping.PUBLIC_HEALTH,
    insuranceType: policy.type,
    policyStatus,
    startDate: policy.attributes.startDate,
  };

  if (policy.attributes.price) {
    header.price = {
      amount: parseFloat(policy.attributes.price),
      billingType: policy.attributes.billingType as BillingType,
    };
  }

  const sections: PolicySingleSection[] = [
    {
      id: 'POLICY_CHECKLISTS',
    },
    {
      id: 'POLICY_DETAILS',
      props: {
        policyType: policy.type,
        info: [
          {
            title: t('myPolicies.policyDetails.providerName', 'Provider'),
            value: policy.providerName,
          },
          {
            title: t('myPolicies.policyDetails.policyNumber', 'Policy number'),
            value: policy.attributes.policyNumber,
          },
          {
            title: t(
              'myPolicies.policyDetails.insuredPerson',
              'Insured person'
            ),
            value:
              policy.attributes.insuredPerson?.name &&
              capitalizeName(policy.attributes.insuredPerson.name),
          },
          {
            title: t('myPolicies.policyDetails.svnr', 'SVNR'),
            value: policy.attributes.SVNR,
          },
        ],
        buttons: [
          { id: 'CONTACT_US', component: ContactUsButton },
          ...(cancelApplicationAllowed
            ? [
                {
                  id: 'PUBLIC_HEALTH_CANCEL_APPLICATION',
                  component: PublicHealthCancelApplicationButton,
                },
              ]
            : []),
        ],
      },
    },
    {
      id: 'POLICY_DOCUMENTS',
      props: {
        documents: getPolicyDocuments(policy),
      },
    },
  ];

  const modals: PolicySingleModal[] = [
    {
      id: 'PUBLIC_HEALTH_CANCEL_APPLICATION',
      component: PublicHealthCancelApplicationModal,
    },
  ];

  return { header, modals, sections };
};
